// CustomTrailsPage — Liste der device-lokal gespeicherten Custom-Trails.
// ======================================================================
//
// Wave 14bv Stage 6j: zeigt alles was der User im Create-Custom-Flow per
// "Save as custom trail" abgelegt hat. Device-local, nicht synchronisiert —
// pro Gerät eigene Liste.
//
// Minimaler Scope: Name, Länge, Datum, Tap → Detail. Delete-Swipe kommt
// später wenn's Bedarf gibt. Rename ebenfalls — für MVP reicht "Name beim
// Save gewählt und fertig".

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listCustomTrails,
  deleteCustomTrail,
  saveCustomTrail,
  setCustomTrailShowOnMap,
  setCustomTrailKmMarkers,
  reverseCustomTrail,
  renameCustomTrail,
  nextKmMarkerState,
  CUSTOM_TRAILS_UPDATED_EVENT,
  type CustomTrail,
  type KmMarkers,
} from '../lib/customTrails';
import { mapIntentsBus } from '../lib/map/mapIntentsBus';
import {
  customTourToGpx,
  downloadGpx,
  parseImportedGpx,
} from '../lib/gpxFormat';

function formatKm(m: number): string {
  return `${(m / 1000).toFixed(1)} km`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function CustomTrailsPage() {
  const [trails, setTrails] = useState<CustomTrail[] | null>(null);
  const navigate = useNavigate();

  // Stage 6m: Tap auf einen Custom-Trail → Intent an die MapPage + Navigate
  // zu "/". MapPage consumed das Intent beim nächsten Render und öffnet den
  // Trail wie eine normale Trail-Selection (inkl. RIDE-Button, Höhenprofil,
  // Navigate-to-start). Kein separater Detail-Screen mehr.
  const handleOpenOnMap = (id: string) => {
    mapIntentsBus.publish({ customTrailId: id, resetMode: true });
    navigate('/');
  };

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      listCustomTrails()
        .then((list) => {
          if (!cancelled) setTrails(list);
        })
        .catch((err) => {
          console.warn('[customTrails] list failed', err);
          if (!cancelled) setTrails([]);
        });
    };
    load();
    // Custom-Trails-Update-Event (nach Save/Delete) → Liste refreshen.
    window.addEventListener(CUSTOM_TRAILS_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(CUSTOM_TRAILS_UPDATED_EVENT, load);
    };
  }, []);

  const totalLength = useMemo(() => {
    if (!trails) return 0;
    return trails.reduce((s, t) => s + t.lengthM, 0);
  }, [trails]);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await deleteCustomTrail(id);
    } catch (err) {
      console.error('[customTrails] delete failed', err);
    }
  };

  // Wave 14cf-fix18: Reverse-Action. Erzeugt eine neue Custom Tour mit
  // umgekehrter Fahrtrichtung. Non-destructive — Original bleibt. User
  // sieht die neue Tour ganz oben in der Liste (neuestes createdAt).
  const handleReverse = async (id: string, name: string) => {
    try {
      const next = await reverseCustomTrail(id);
      if (!next) {
        console.warn('[customTrails] reverse: source not found', id);
        return;
      }
      // Liste refresht sich von selbst via CUSTOM_TRAILS_UPDATED_EVENT.
      console.info(`[customTrails] reversed "${name}" → "${next.name}"`);
    } catch (err) {
      console.error('[customTrails] reverse failed', err);
    }
  };

  // Wave 14dn: Inline-Rename per kleinem Bleistift-Button links neben dem
  // Augen-Symbol. window.prompt für Quick-Path — keine extra Modal-State-
  // Verdrahtung, der User editiert in-place und die Liste refresht sich
  // via CUSTOM_TRAILS_UPDATED_EVENT.
  const handleRename = async (id: string, currentName: string) => {
    const next = window.prompt('Rename tour:', currentName);
    if (next == null) return; // User hat abgebrochen
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      await renameCustomTrail(id, trimmed);
      console.info(`[customTrails] renamed "${currentName}" → "${trimmed}"`);
    } catch (err) {
      console.error('[customTrails] rename failed', err);
    }
  };

  // Wave 14ca-fix42 (PRO): GPX-Import. User waehlt eine .gpx-Datei,
  // parsed wird sie zu coords+lengthM, gespeichert als Custom Trail.
  // Fehler werden inline gezeigt (kein Crash bei kaputtem GPX).
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // erlaubt re-select desselben Files spaeter
    if (!file) return;
    setImportError(null);
    setImporting(true);
    try {
      const xml = await file.text();
      const parsed = parseImportedGpx(xml);
      const fallbackName = file.name.replace(/\.gpx$/i, '');
      const name = parsed.name?.trim() || fallbackName || 'Imported route';
      // Wave 14cf-fix5: Elevation pro Punkt mitspeichern, wenn die GPX
      // sie liefert. Vorher (Bug): parsed.elevations[] wurde komplett
      // verworfen → useTrailElevation fiel auf SRTM zurück → bei der
      // Komoot-Route "Elba zum airport" zeigte die App 1734 m HM statt
      // der tatsächlichen ~1010 m, weil SRTM für die Route an mehreren
      // Stellen Cell-Drift hatte. Mit dem 3-Tupel [lon, lat, ele] greift
      // hasElevationData() im Hook und das Profil nutzt die akkuraten
      // Werte aus der GPX direkt.
      const coords = parsed.coords.map((c, i): [number, number] | [number, number, number] => {
        const ele = parsed.elevations[i];
        return ele != null && Number.isFinite(ele) ? [c[0], c[1], ele] : c;
      });
      await saveCustomTrail({
        name,
        coords,
        lengthM: parsed.lengthM,
        difficulty: 'unknown',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown_error';
      setImportError(
        msg === 'too_few_points'
          ? 'GPX needs at least 2 track points.'
          : msg.startsWith('invalid_xml')
            ? 'GPX file is malformed.'
            : `Import failed: ${msg}`,
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className="absolute inset-0 overflow-auto bg-turbo-900"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
    >
      <div className="px-4 pb-6">
        <div className="mb-3 flex items-center gap-2">
          <Link
            to="/navi"
            className="inline-flex items-center gap-1 rounded-full bg-turbo-700/40 border border-sky-500/30 text-sky-300 text-sm px-3 py-1.5 no-underline"
          >
            <span aria-hidden>←</span> NAVI
          </Link>
        </div>
        <h1 className="text-xl font-semibold text-white flex items-baseline gap-2">
          My Custom Tours
          {trails && (
            <span className="text-xs text-white/45 tabular-nums font-normal">
              {trails.length}
              {trails.length > 0 && ` · ${formatKm(totalLength)} total`}
            </span>
          )}
        </h1>
        <p className="text-white/55 text-xs mt-1">
          Routes you've built via Create Custom — stored on this device only.
        </p>
        {/* Wave 14ca-fix42: Import-GPX-Button (PRO). Hidden file input wird
            via Button getriggert. Erlaubt nur .gpx-Suffix als Hint, aber wir
            parsen alles als XML — User sind kreativ. */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleImportClick}
            disabled={importing}
            className="text-xs text-sky-300 hover:text-sky-200 disabled:text-white/30 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg border border-sky-500/30 hover:border-sky-500/50 disabled:border-turbo-500/20 transition-colors flex items-center gap-1.5"
          >
            {importing ? 'Importing…' : '↑ Import GPX'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            onChange={handleFileSelected}
            className="hidden"
          />
        </div>
        {importError && (
          <div className="mt-2 rounded-lg border border-red-500/40 bg-red-500/15 p-2 text-red-200 text-xs">
            {importError}
          </div>
        )}
      </div>

      <div className="px-4">
        {trails === null && (
          <div className="text-white/50 text-sm">Loading…</div>
        )}
        {trails && trails.length === 0 && (
          <div className="rounded-xl border border-turbo-700/40 bg-turbo-700/20 p-5 text-white/70 text-sm">
            No custom tours yet. Go to{' '}
            <Link
              to="/navi/custom"
              className="text-sky-300 underline decoration-sky-300/40 underline-offset-2"
            >
              Create custom
            </Link>{' '}
            to build one, then tap "Save as custom trail" on the result.
          </div>
        )}
        {trails && trails.length > 0 && (
          <ul className="flex flex-col gap-2">
            {trails.map((t) => (
              <li
                key={t.id}
                className="rounded-xl border border-turbo-700/40 bg-turbo-700/20 overflow-hidden"
              >
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => handleOpenOnMap(t.id)}
                    className="flex-1 p-3 text-left bg-transparent"
                  >
                    <div className="text-white font-medium text-[15px] leading-tight">
                      {t.name}
                    </div>
                    <div className="mt-1 text-white/60 text-xs">
                      {formatKm(t.lengthM)} · {formatDate(t.createdAt)}
                      {t.trailsUsed.length > 0 &&
                        ` · ${t.trailsUsed.length} trails`}
                    </div>
                  </button>
                  {/* Wave 14dn: Rename-Button (kleiner Bleistift), links
                      neben dem Augen-Symbol. Tap → window.prompt mit dem
                      aktuellen Namen vorausgefüllt, Submit speichert. */}
                  <button
                    type="button"
                    onClick={() => handleRename(t.id, t.name)}
                    className="px-2.5 text-sky-300/80 hover:text-sky-200 active:bg-sky-500/10 flex items-center justify-center"
                    aria-label={`Rename ${t.name}`}
                    title="Rename tour"
                  >
                    <IconPencil />
                  </button>
                  {/* Wave 14cf-fix11/12: Show-on-Map-Toggle (ghostbusters-
                      style off-state) + km-Marker-Carousel (1 / 1km / 10km).
                      Tap auf das Auge togglet sichtbar/unsichtbar; wenn
                      sichtbar, erscheint daneben der km-Carousel-Knopf. */}
                  <ShowOnMapToggle
                    trail={t}
                    onChange={(next) => {
                      setTrails((prev) =>
                        prev
                          ? prev.map((x) => (x.id === t.id ? next : x))
                          : prev,
                      );
                    }}
                  />
                  {/* Wave 14ca-fix44 (PRO): Export-Knopf neben Delete, damit
                      User die Tour als GPX runterladen koennen ohne erst ins
                      Detail zu navigieren. Ein Tap = .gpx-Download. */}
                  <button
                    type="button"
                    onClick={async () => {
                      const gpx = await customTourToGpx(t);
                      downloadGpx(`adt-tour-${t.name}`, gpx);
                    }}
                    className="px-3 text-sky-300/80 hover:text-sky-200 text-base active:bg-sky-500/10"
                    aria-label={`Export ${t.name} as GPX`}
                    title="Export GPX"
                  >
                    ↓
                  </button>
                  {/* Wave 14cf-fix18: Reverse-Button. Speichert eine neue
                      umgedrehte Kopie der Tour. Inline-SVG im gleichen
                      Lucide-Stil wie die NaviPage-Icons (24×24, stroke-width
                      2, currentColor) — visuell deckungsgleich mit lucide-
                      react's `ArrowLeftRight`, aber ohne neue Dependency. */}
                  <button
                    type="button"
                    onClick={() => handleReverse(t.id, t.name)}
                    className="px-3 text-sky-300/80 hover:text-sky-200 active:bg-sky-500/10 flex items-center justify-center"
                    aria-label={`Reverse direction of ${t.name}`}
                    title="Reverse direction"
                  >
                    <IconArrowLeftRight />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id, t.name)}
                    className="px-3 text-rose-300/70 text-xl active:bg-rose-500/10"
                    aria-label={`Delete ${t.name}`}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Wave 14cf-fix11/12: Auge + km-Marker-Carousel als komposite Komponente.
 *
 * Ghostbusters-Off-State: Auge mit rotem Schrägstrich (klar als
 * "verboten / aus" lesbar). Der vorherige 👁️‍🗨️-Glyph hat User
 * verwirrt weil "Auge in Sprechblase" nicht klar als "off" liest.
 *
 * Im On-State erscheint zusätzlich der km-Carousel-Button rechts vom
 * Auge: durchgestrichene 1 (off) → 1 (1km-marker) → 10 (10km-marker)
 *  → durchgestrichene 1 zurück.
 */
function ShowOnMapToggle({
  trail,
  onChange,
}: {
  trail: CustomTrail;
  onChange: (next: CustomTrail) => void;
}) {
  const visible = trail.showOnMap === true;
  const km = (trail.kmMarkers ?? 'off') as KmMarkers;

  const toggleVisibility = async () => {
    const updated = await setCustomTrailShowOnMap(trail.id, !visible);
    if (updated) onChange(updated);
  };
  const cycleKm = async () => {
    const next = nextKmMarkerState(km);
    const updated = await setCustomTrailKmMarkers(trail.id, next);
    if (updated) onChange(updated);
  };

  return (
    <div className="flex items-stretch">
      <button
        type="button"
        onClick={toggleVisibility}
        className={`px-2.5 flex items-center justify-center transition-colors ${
          visible
            ? 'text-sky-300'
            : 'text-white/40 hover:text-white/60'
        }`}
        aria-label={`Toggle show-on-map for ${trail.name}`}
        aria-pressed={visible}
        title={
          visible
            ? 'Auf Karte sichtbar — Tap zum Ausblenden'
            : 'Nicht auf Karte sichtbar — Tap zum Einblenden'
        }
      >
        <EyeIcon active={visible} />
      </button>
      {visible && (
        <button
          type="button"
          onClick={cycleKm}
          className={`px-1.5 min-w-[34px] flex items-center justify-center text-xs font-bold transition-colors ${
            km === 'off'
              ? 'text-white/40 hover:text-white/60'
              : 'text-sky-300'
          }`}
          aria-label={`Cycle km-markers for ${trail.name}: currently ${km}`}
          title={
            km === 'off'
              ? 'km-Marker aus — Tap für 1 km'
              : km === '1km'
                ? 'Marker alle 1 km — Tap für 10 km'
                : 'Marker alle 10 km — Tap zum Ausschalten'
          }
        >
          <KmMarkerLabel state={km} />
        </button>
      )}
    </div>
  );
}

/** Auge-Icon mit rotem Schrägstrich-Overlay im Off-State. */
function EyeIcon({ active }: { active: boolean }) {
  return (
    <span
      className="relative inline-flex items-center justify-center w-6 h-6"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-5 h-5"
      >
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      {!active && (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <span
            className="block w-7 h-[2.5px] bg-rose-500 rounded-full"
            style={{ transform: 'rotate(-45deg)' }}
          />
        </span>
      )}
    </span>
  );
}

/** Label für den km-Carousel-Button. */
function KmMarkerLabel({ state }: { state: KmMarkers }) {
  if (state === 'off') {
    // Durchgestrichene 1 — visuelles Pendant zum Off-Auge.
    return (
      <span className="relative inline-block">
        <span>1</span>
        <span
          aria-hidden="true"
          className="absolute left-[-2px] right-[-2px] top-1/2 h-[2px] bg-rose-500 rounded-full"
          style={{ transform: 'translateY(-50%) rotate(-15deg)' }}
        />
      </span>
    );
  }
  return <span>{state === '1km' ? '1' : '10'}</span>;
}

/**
 * Wave 14cf-fix18: ArrowLeftRight-Icon für den Reverse-Button.
 *
 * Pfade aus lucide-react's `ArrowLeftRight` 1:1 übernommen (zwei horizontale
 * Pfeile, oben nach rechts, unten nach links — semantisch "Richtung
 * umkehren"). Inline statt Dep, damit kein neuer Lib-Import nötig wird.
 * Stroke-Style identisch zu NaviPage-Icons (currentColor, width 2,
 * round caps + joins).
 */
function IconArrowLeftRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="m16 3 4 4-4 4" />
      <path d="M20 7H4" />
      <path d="m8 21-4-4 4-4" />
      <path d="M4 17h16" />
    </svg>
  );
}

/** Wave 14dn: Bleistift-Icon für Rename. Klassisches Lucide-Edit-Shape. */
function IconPencil() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}
